import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaShieldAlt, FaRocket, FaStar, FaCheckCircle } from 'react-icons/fa';

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden min-h-[90vh] flex items-center">
      {/* Background layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/30" />
      <div className="absolute -left-32 top-20 w-96 h-96 rounded-full bg-primary-400/15 blur-3xl animate-glow" />
      <div className="absolute -right-32 bottom-10 w-[500px] h-[500px] rounded-full bg-indigo-400/10 blur-3xl animate-glow" style={{ animationDelay: '1.5s' }} />
      <div className="absolute left-1/2 top-1/3 w-64 h-64 rounded-full bg-sky-300/10 blur-3xl animate-glow" style={{ animationDelay: '3s' }} />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, #3b82f6 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />

      <div className="container-custom relative py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="hero-badge mb-8 inline-flex items-center gap-2">
              <FaRocket className="text-primary-500" />
              Fast delivery nationwide
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
              <span className="text-slate-900 dark:text-white">Discover </span>
              <span className="text-gradient">Premium</span>
              <br />
              <span className="text-slate-900 dark:text-white">Smartphones</span>
            </h1>

            <p className="mt-6 max-w-lg text-lg text-slate-500 dark:text-slate-400 leading-relaxed">
              Shop the latest mobile devices with confidence. Curated offers, expert guidance, and seamless checkout for every device.
            </p>

            {/* Trust indicators */}
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-500" /> PTA Approved</span>
              <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-500" /> Official Warranty</span>
              <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-500" /> Secure Payment</span>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/products" className="btn-primary group">
                Browse Devices
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact Sales
              </Link>
            </div>

            {/* Stats row */}
            <div className="mt-14 grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 dark:text-white">500<span className="text-primary-500">+</span></div>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Products</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 dark:text-white">50<span className="text-primary-500">+</span></div>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Brands</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 dark:text-white">10K<span className="text-primary-500">+</span></div>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Happy Customers</p>
              </div>
            </div>
          </motion.div>

          {/* Right side - 3D Phone mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex justify-center items-center"
          >
            {/* Glowing background orbs */}
            <div className="absolute w-72 h-72 bg-primary-500/20 rounded-full blur-[80px] animate-pulse-soft" />
            <div className="absolute w-56 h-56 bg-indigo-500/15 rounded-full blur-[60px] translate-x-12 -translate-y-8 animate-pulse-soft" style={{ animationDelay: '2s' }} />

            {/* Phone container with 3D perspective */}
            <div className="phone-perspective relative z-10">
              <div className="phone-3d animate-float">
                {/* Phone frame */}
                <div className="relative w-[280px] h-[570px] sm:w-[300px] sm:h-[610px] rounded-[3rem] bg-gradient-to-b from-slate-800 to-slate-900 p-[3px] shadow-2xl shadow-slate-900/40">
                  <div className="w-full h-full rounded-[2.85rem] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden relative">
                    {/* Notch / Dynamic Island */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-20" />
                    
                    {/* Screen content - gradient wallpaper */}
                    <div className="w-full h-full bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 flex flex-col items-center justify-center p-6 relative overflow-hidden">
                      {/* Screen decorative elements */}
                      <div className="absolute top-0 left-0 w-full h-full">
                        <div className="absolute top-[30%] left-[20%] w-32 h-32 rounded-full bg-white/10 blur-2xl" />
                        <div className="absolute bottom-[20%] right-[10%] w-40 h-40 rounded-full bg-purple-300/15 blur-2xl" />
                      </div>

                      {/* Mock app icons grid */}
                      <div className="relative z-10 mt-16 grid grid-cols-4 gap-4">
                        {[
                          'bg-red-500', 'bg-green-500', 'bg-blue-500', 'bg-yellow-500',
                          'bg-purple-500', 'bg-pink-500', 'bg-cyan-500', 'bg-orange-500',
                          'bg-teal-500', 'bg-indigo-500', 'bg-emerald-500', 'bg-rose-500',
                        ].map((color, i) => (
                          <div key={i} className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl ${color} opacity-80 shadow-lg`} />
                        ))}
                      </div>

                      {/* Time display */}
                      <div className="absolute top-12 text-center text-white z-10">
                        <div className="text-5xl font-thin tracking-wider">9:41</div>
                        <div className="text-xs mt-1 opacity-70">Thursday, January 1</div>
                      </div>

                      {/* Bottom bar */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-32 h-1 rounded-full bg-white/30 z-10" />
                    </div>
                  </div>
                </div>

                {/* Reflective shine effect */}
                <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none" />
              </div>
            </div>

            {/* Floating feature cards */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="absolute -left-4 top-16 lg:-left-8"
            >
              <div className="glass-card px-4 py-3 flex items-center gap-3 animate-float-delay">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <FaShieldAlt className="text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Warranty</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Official guaranteed</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="absolute -right-4 bottom-24 lg:-right-8"
            >
              <div className="glass-card px-4 py-3 flex items-center gap-3 animate-float" style={{ animationDelay: '1s' }}>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <FaStar className="text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">4.9 Rating</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">2000+ reviews</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;